import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Paragraph } from '../models/Paragraph';
import { TextElement } from '../models/TranscriptElement';

/**
 * Command state for MergeParagraphsCommand
 * Captures the state needed for undo operations
 */
interface MergeParagraphState {
  segmentId: string;
  firstParagraphId: string;
  secondParagraphId: string;
  firstElements: any[];
  firstEndTime: number;
  secondElements: any[];
}

/**
 * Command to merge two consecutive paragraphs
 * 
 * This command combines two paragraphs into a single paragraph, merging
 * their elements and updating timing accordingly.
 */
export class MergeParagraphsCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the paragraphs */
  private segmentId: string;
  
  /** ID of the first paragraph to merge */
  private firstParagraphId: string;
  
  /** ID of the second paragraph to merge */
  private secondParagraphId: string;
  
  /** User ID for operation tracking */
  private userId: string;
  
  /** State captured for undo operations */
  private state: MergeParagraphState | null = null;
  
  /**
   * Creates a new MergeParagraphsCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the paragraphs
   * @param firstParagraphId ID of the first paragraph to merge
   * @param secondParagraphId ID of the second paragraph to merge
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    firstParagraphId: string,
    secondParagraphId: string,
    userId: string
  ) {
    super(`Merge paragraphs ${firstParagraphId} and ${secondParagraphId}`, editor);
    
    this.segmentId = segmentId;
    this.firstParagraphId = firstParagraphId;
    this.secondParagraphId = secondParagraphId;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('firstParagraphId', firstParagraphId);
    this.setMetadata('secondParagraphId', secondParagraphId);
    this.setMetadata('userId', userId);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraphs
      const firstParagraph = segment.getParagraph(this.firstParagraphId);
      const secondParagraph = segment.getParagraph(this.secondParagraphId);
      
      if (!firstParagraph || !secondParagraph) {
        return false;
      }
      
      // Store original state for undo
      const firstElements = [...firstParagraph.elements];
      const firstEndTime = firstParagraph.endTime;
      const secondElements = [...secondParagraph.elements];
      
      this.state = {
        segmentId: this.segmentId,
        firstParagraphId: this.firstParagraphId,
        secondParagraphId: this.secondParagraphId,
        firstElements,
        firstEndTime,
        secondElements
      };
      
      // Create copies of elements to avoid reference issues
      const copiedSecondElements = secondElements.map(el => {
        const copy = { ...el };
        copy.paragraphId = this.firstParagraphId; // Update the paragraph ID
        return copy;
      });
      
      // Extend the first paragraph with the elements from the second
      firstParagraph.elements = [...firstParagraph.elements, ...copiedSecondElements];
      
      // Update the end time to include the second paragraph
      firstParagraph.endTime = secondParagraph.endTime;
      
      // Rebuild element map in the paragraph (important!)
      firstParagraph.rebuildElementMap();
      
      // Remove the second paragraph
      segment.removeParagraph(this.secondParagraphId);
      
      // Record the operation
      this.editor.recordOperation({
        type: 'paragraph_merge',
        segmentId: this.segmentId,
        paragraphId: this.firstParagraphId,
        data: {
          secondParagraphId: this.secondParagraphId,
          originalEndTime: firstEndTime,
          newEndTime: firstParagraph.endTime
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_MERGED, {
        segment,
        paragraph: firstParagraph,
        mergedParagraphId: this.secondParagraphId
      });
      
      return true;
    } catch (error) {
      console.error('Error executing MergeParagraphsCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      if (!this.state) {
        console.error('Cannot undo MergeParagraphsCommand: state is null');
        return false;
      }
      
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the first paragraph
      const firstParagraph = segment.getParagraph(this.firstParagraphId);
      if (!firstParagraph) {
        return false;
      }
      
      // Restore the original elements to the first paragraph
      firstParagraph.elements = [...this.state.firstElements];
      firstParagraph.endTime = this.state.firstEndTime;
      
      // Rebuild element map
      firstParagraph.rebuildElementMap();
      
      // Recreate the second paragraph
      const ParagraphClass = this.editor.getParagraphClass();
      const restoredParagraph = new ParagraphClass(
        this.secondParagraphId,
        // Need to calculate timing based on elements
        this.findMinStartTime(this.state.secondElements),
        this.findMaxEndTime(this.state.secondElements),
        [...this.state.secondElements],
        this.segmentId
      );
      
      // Add the restored paragraph to the segment
      segment.addParagraph(restoredParagraph);
      
      // Sort paragraphs by time to ensure proper ordering
      this.editor.sortParagraphsByTime(segment);
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_SPLIT, {
        segment,
        originalParagraph: firstParagraph,
        newParagraph: restoredParagraph
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing MergeParagraphsCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Helper method to find the minimum start time from an array of elements
   */
  private findMinStartTime(elements: any[]): number {
    const textElements = elements.filter(el => el.type === 'text');
    const startTimes = textElements
      .map(el => el.startTime)
      .filter(time => time !== undefined) as number[];
    
    return startTimes.length > 0 ? Math.min(...startTimes) : 0;
  }
  
  /**
   * Helper method to find the maximum end time from an array of elements
   */
  private findMaxEndTime(elements: any[]): number {
    const textElements = elements.filter(el => el.type === 'text');
    const endTimes = textElements
      .map(el => el.endTime)
      .filter(time => time !== undefined) as number[];
    
    return endTimes.length > 0 ? Math.max(...endTimes) : 0;
  }
}